<template>
  <div class="materiel-affectations">
    <h6>Affectations du matériel</h6>

    <!-- Affectation en cours -->
    <div v-if="materiel.materiel_collab_id" class="alert alert-success mb-3">
      <div class="d-flex align-items-center">
        <i class="fas fa-user-check mr-2"></i>
        <strong>Affecté actuellement à :</strong>
        <span class="ml-2">
          {{ materiel.collaborateur.collaborateur_nom }}
          {{ materiel.collaborateur.collaborateur_prenom }}
        </span>
      </div>
    </div>
    <div v-else class="alert alert-warning mb-3">
      <div class="d-flex align-items-center">
        <i class="fas fa-exclamation-triangle mr-2"></i>
        <strong>Matériel non affecté</strong>
      </div>
    </div>

    <BaseToolBar title="Affectations" subtitle="Gérer les affectations">
      <button
        type="button"
        class="btn btn-sm btn-primary"
        @click="nouvAffectation"
        :disabled="loading"
      >
        <span
          v-if="loading"
          class="spinner-border spinner-border-sm mr-1"
        ></span>
        <i v-else class="fas fa-plus"></i>
        {{ loading ? "Ajout..." : "Nouvelle affectation" }}
      </button>
    </BaseToolBar>

    <div v-if="loading" class="text-center py-3">
      <i class="fas fa-spinner fa-spin"></i> Chargement...
    </div>

    <div
      v-else-if="affectations.length === 0"
      class="text-center py-3 text-muted"
    >
      Aucune affectation
    </div>

    <div v-else>
      <p class="text-info">
        {{ affectations.length }} affectation(s) trouvée(s)
      </p>

      <div
        v-for="affectation in affectations"
        :key="affectation.id"
        class="mb-3"
      >
        <div class="card">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <AppAvatar
                  :url="affectation.collaborateur.collaborateur_avatar"
                  propSize="3rem"
                  class="mr-3"
                ></AppAvatar>
                <div>
                  <strong>{{
                    affectation.collaborateur.collaborateur_nom +
                      " " +
                      affectation.collaborateur.collaborateur_prenom
                  }}</strong
                  ><br />
                  <small class="text-muted">
                    {{ formatDate(affectation.date_debut) }}
                    {{
                      affectation.date_fin
                        ? " → " + formatDate(affectation.date_fin)
                        : ""
                    }}
                  </small>
                </div>
              </div>
              <div>
                <button
                  class="btn btn-sm btn-primary"
                  @click="editAffectation(affectation)"
                >
                  Ouvrir
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Bootstrap simple -->
    <div
      v-if="showModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0,0,0,0.5);"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editing ? "Modifier" : "Nouvelle" }} affectation
            </h5>
            <button type="button" class="close" @click="fermerModal">
              <span>&times;</span>
            </button>
          </div>
          <form @submit.prevent="sauvegarder">
            <div class="modal-body">
              <div class="form-group">
                <label>Collaborateur *</label>
                <select-collaborateur
                  v-model.number="form.collaborateur_id"
                ></select-collaborateur>
              </div>
              <div class="row">
                <div class="col-6">
                  <div class="form-group">
                    <label>Date début *</label>
                    <input
                      type="date"
                      class="form-control"
                      v-model="form.date_debut"
                      required
                    />
                  </div>
                </div>
                <div class="col-6">
                  <div class="form-group">
                    <label>Date fin</label>
                    <input
                      type="date"
                      class="form-control"
                      v-model="form.date_fin"
                    />
                  </div>
                </div>
              </div>
              <div class="form-group">
                <uploader
                  v-if="form.id"
                  class="mt-5"
                  id="document_materiel_affectation"
                  url="/upload/document"
                  title="Fiche de remise de matériel"
                  :file_system="'MATERIEL_AFFECTATION'"
                  :file_field="'materiel_affectation'"
                  :file_key="form.id"
                ></uploader>
                <div v-else class="alert alert-info mt-5">
                  <i class="fas fa-info-circle mr-2"></i>
                  Veuillez d'abord enregistrer l'affectation pour pouvoir
                  ajouter une fiche de remise de matériel.
                </div>
              </div>
              <div class="form-group">
                <label>Commentaire</label>
                <textarea
                  class="form-control"
                  rows="3"
                  v-model="form.commentaire"
                ></textarea>
              </div>
            </div>
            <div class="modal-footer">
              <button
                v-if="editing"
                type="button"
                class="btn btn-danger mr-auto"
                @click="supprimer"
              >
                Supprimer
              </button>
              <button
                type="button"
                class="btn btn-secondary"
                @click="fermerModal"
              >
                Annuler
              </button>
              <button
                type="submit"
                class="btn btn-success"
                :disabled="!form.collaborateur_id"
              >
                {{ editing ? "Modifier" : "Créer" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseToolBar from "@/components/bases/ToolBar.vue";
import SelectCollaborateur from "@/components/bases/SelectCollaborateur.vue";
import AppAvatar from "@/components/bases/Avatar.vue";
import Uploader from "@/components/bases/Uploader.vue";

export default {
  components: {
    BaseToolBar,
    SelectCollaborateur,
    AppAvatar,
    Uploader
  },
  props: {
    materiel: {
      type: Object,
      required: true
    },
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      affectations: [],
      loading: false,
      editing: false,
      showModal: false,
      form: this.getDefaultForm()
    };
  },
  mounted() {
    this.loadAffectations();
  },
  watch: {
    "materiel.id": {
      handler() {
        if (this.materiel.id) {
          this.loadAffectations();
        }
      }
    }
  },
  methods: {
    getDefaultForm() {
      return {
        id: null,
        collaborateur_id: null,
        date_debut: new Date().toISOString().substr(0, 10),
        date_fin: null,
        commentaire: ""
      };
    },

    async loadAffectations() {
      if (!this.materiel.id) return;

      console.log("loadAffectations called for materiel ID:", this.materiel.id);
      this.loading = true;
      try {
        const response = await this.$http.get(
          `/materiels/materielcollaborateur/${this.materiel.id}`
        );
        console.log("Response reçue:", response.data);
        const newAffectations = response.data.data || response.data || [];
        console.log("Nouvelles affectations:", newAffectations);

        // Force reactivity with Vue.set or array replacement
        this.$set(this, "affectations", [...newAffectations]);
        console.log("Affectations après $set:", this.affectations);

        // Force template update
        this.$forceUpdate();
        console.log("ForceUpdate appelé");
      } catch (error) {
        console.error("Erreur:", error);
        this.affectations = [];
      } finally {
        this.loading = false;
      }
    },

    nouvAffectation() {
      this.editing = false;
      this.form = this.getDefaultForm();
      this.showModal = true;
    },

    editAffectation(affectation) {
      this.editing = true;
      this.form = { ...affectation };
      this.showModal = true;
    },

    fermerModal() {
      this.showModal = false;
    },

    async sauvegarder() {
      if (!this.form.collaborateur_id) {
        console.error("Sélectionnez un collaborateur");
        return;
      }

      try {
        const payload = {
          materiel_id: this.materiel.id,
          collaborateur_id: this.form.collaborateur_id,
          date_debut: this.form.date_debut,
          date_fin: this.form.date_fin || null,
          commentaire: this.form.commentaire || null
        };

        if (this.editing) {
          console.log("=== MODIFICATION ===");
          const modifyPayload = { ...payload, id: this.form.id };
          await this.$http.put(
            `/materiels/materielcollaborateur`,
            modifyPayload
          );
          console.log("Modifiée !");
        } else {
          console.log("=== CRÉATION ===");
          const result = await this.$http.post(
            "/materiels/materielcollaborateur",
            payload
          );
          console.log("Résultat création:", result);
          console.log("Créée !");
        }

        console.log("=== AVANT RELOAD ===");
        await this.loadAffectations();
        console.log("=== APRÈS RELOAD ===");

        // Émettre l'événement pour recharger la liste des matériels
        console.log("=== EMIT UPDATE ===", this.materiel);
        this.$emit("update", this.materiel);
        console.log("=== EMIT ENVOYÉ ===");

        this.fermerModal();
      } catch (error) {
        console.error("Erreur:", error);
        console.error("Erreur lors de la sauvegarde");
      }
    },

    async supprimer() {
      if (!confirm("Supprimer cette affectation ?")) return;

      try {
        await this.$http.delete(
          `/materiels/materielcollaborateur/${this.form.id}`
        );
        console.log("Supprimée !");
        await this.loadAffectations();

        // Émettre l'événement pour recharger la liste des matériels
        console.log("=== EMIT UPDATE SUPPRESSION ===", this.materiel);
        this.$emit("update", this.materiel);
        console.log("=== EMIT SUPPRESSION ENVOYÉ ===");

        this.fermerModal();
      } catch (error) {
        console.error("Erreur:", error);
        console.error("Erreur lors de la suppression");
      }
    },

    getCollabName(id) {
      console.log("getCollabName appelé avec id:", id);
      console.log("this.data.users disponible:", this.data.users);
      if (!id || !this.data.users) return "Inconnu";
      const user = this.data.users.find(u => u.id === id);
      console.log("User trouvé:", user);
      return user
        ? `${user.collaborateur_nom} ${user.collaborateur_prenom}`
        : "Inconnu";
    },

    getCollabNameById(collaborateur_id) {
      if (!collaborateur_id || !this.data.users) return "Collaborateur inconnu";
      const user = this.data.users.find(u => u.id === collaborateur_id);
      return user
        ? `${user.collaborateur_prenom} ${user.collaborateur_nom}`
        : "Collaborateur inconnu";
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("fr-FR");
    }
  }
};
</script>

<style scoped>
.materiel-affectations {
  padding: 20px;
}

h6 {
  color: #333;
  margin-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 0.5rem;
}

.badge {
  font-size: 0.75rem;
}

@media (max-width: 768px) {
  .materiel-affectations {
    padding: 10px;
  }
}
</style>
